import { booleanContains } from "@turf/boolean-contains";
import { booleanOverlap } from "@turf/boolean-overlap";
import { booleanWithin } from "@turf/boolean-within";
import { polygon as turfPolygon } from "@turf/helpers";
import { POLYGON_TYPES } from "./GoogleMap.interface";
export const convertGmapPathToGeoJSONPath = (polygonList) => {
    const geoJSONPolygons = [];
    polygonList.forEach((poly) => {
        const coords = [];
        for (const point of poly.path) {
            coords.push([point.lng, point.lat]);
        }
        const closedCoords = coords.length > 0 &&
            (coords[0][0] !== coords[coords.length - 1][0] || coords[0][1] !== coords[coords.length - 1][1])
            ? [...coords, [coords[0][0], coords[0][1]]]
            : coords;
        geoJSONPolygons.push({
            geofenceId: poly.geofenceId,
            geofenceType: poly.geofenceType,
            path: closedCoords
        });
    });
    return geoJSONPolygons;
};
export const convertToGeoJSONPath = (polygonList) => {
    const geoJSONPolygons = [];
    polygonList.forEach((poly) => {
        const geoJSONPaths = [];
        for (const point of poly.paths) {
            geoJSONPaths.push([point.lng, point.lat]);
        }
        const closedGeoJSONPaths = geoJSONPaths.length > 0 &&
            (geoJSONPaths[0][0] !== geoJSONPaths[geoJSONPaths.length - 1][0] ||
                geoJSONPaths[0][1] !== geoJSONPaths[geoJSONPaths.length - 1][1])
            ? [...geoJSONPaths, [geoJSONPaths[0][0], geoJSONPaths[0][1]]]
            : geoJSONPaths;
        geoJSONPolygons.push({
            id: poly.id,
            path: closedGeoJSONPaths,
            polygonType: poly.polygonType
        });
    });
    return geoJSONPolygons;
};
export const getGeofenceTypeName = (geofenceType) => {
    switch (geofenceType) {
        case POLYGON_TYPES.PROJECT_AREA:
            return "Project Area";
        case POLYGON_TYPES.JOB_SITE:
            return "Job Site";
        case POLYGON_TYPES.CHARGE_STATION:
            return "Charge Station";
        default:
            return "Unknown";
    }
};
const checkAllRestrictedPolygonsInsideProjectArea = (savedChargeStationOrWorkSitePolygons, ProjectAreaPolygon) => {
    const containsAllRestrictedPolygons = savedChargeStationOrWorkSitePolygons.every((polygon) => booleanContains(ProjectAreaPolygon, turfPolygon([polygon.path])));
    if (!containsAllRestrictedPolygons) {
        return { isValid: false, message: "All charge stations and job sites must be inside the project area." };
    }
    return { isValid: true, message: "" };
};
const validateProjectAreaPolygon = (savedProjectAreaPolygons, savedChargeStationOrWorkSitePolygons, ProjectAreaPolygon) => {
    // validation to allow only one project area
    if (savedProjectAreaPolygons.length > 0) {
        return { isValid: false, message: "Only one project area is allowed." };
    }
    // validation for all work site or charge station(Restricted polygons) must be inside the new project area
    if (savedChargeStationOrWorkSitePolygons.length > 0) {
        return checkAllRestrictedPolygonsInsideProjectArea(savedChargeStationOrWorkSitePolygons, ProjectAreaPolygon);
    }
    return { isValid: true, message: "" };
};
const validateRestrictedPolygons = (savedChargeStationOrWorkSitePolygons, validateTurfPolygon, savedProjectAreaPolygons) => {
    // check only one project area exists
    if (savedProjectAreaPolygons.length === 1) {
        const projectArea = turfPolygon([savedProjectAreaPolygons[0].path]);
        // Check if restricted polygon is inside the project area or not
        if (!booleanContains(projectArea, validateTurfPolygon)) {
            return { isValid: false, message: "Charge stations or work sites must be inside the project area." };
        }
    }
    // Validate that restricted polygons do not overlap or contain one another
    for (const polygon of savedChargeStationOrWorkSitePolygons) {
        const existingTurfPolygon = turfPolygon([polygon.path]);
        const overlaps = booleanOverlap(existingTurfPolygon, validateTurfPolygon);
        // Check if one polygon contains or is within the other
        const containsEitherPolygon = booleanContains(existingTurfPolygon, validateTurfPolygon) ||
            booleanContains(validateTurfPolygon, existingTurfPolygon) ||
            booleanWithin(existingTurfPolygon, validateTurfPolygon) ||
            booleanWithin(validateTurfPolygon, existingTurfPolygon);
        if (overlaps || containsEitherPolygon) {
            return {
                isValid: false,
                message: "Charge station or work site polygons must not overlap or contain one another."
            };
        }
    }
    return { isValid: true, message: "" };
};
export const checkIsValidPolygon = (validatePolygon, polygonList) => {
    let validationResult = { isValid: true, message: "" };
    if (validatePolygon.paths.length < 3) {
        return { isValid: false, message: "Polygon must have at least 3 points." };
    }
    const [validatePolygonInfo] = convertToGeoJSONPath([validatePolygon]);
    const savedPolygons = convertToGeoJSONPath(polygonList.filter(({ id }) => id !== validatePolygon.id));
    const savedProjectAreaPolygons = savedPolygons.filter(({ polygonType }) => polygonType === POLYGON_TYPES.PROJECT_AREA);
    const savedChargeStationOrWorkSitePolygons = savedPolygons.filter(({ polygonType }) => polygonType !== POLYGON_TYPES.PROJECT_AREA);
    const validateTurfPolygon = turfPolygon([validatePolygonInfo.path]);
    const isPolygonListEmpty = polygonList.length === 0;
    const isProjectAreaPolygonValidation = validatePolygonInfo.polygonType === POLYGON_TYPES.PROJECT_AREA;
    // validation for restricted(charge station or work site) polygons
    const isRestrictedPolygonValidation = validatePolygonInfo.polygonType !== POLYGON_TYPES.PROJECT_AREA;
    // validation for If there are no existing polygons, the new polygon is automatically valid.
    if (isPolygonListEmpty) {
        return { isValid: true, message: "" };
    }
    // validations for project area polygon
    if (isProjectAreaPolygonValidation) {
        validationResult = validateProjectAreaPolygon(savedProjectAreaPolygons, savedChargeStationOrWorkSitePolygons, validateTurfPolygon);
        if (!validationResult.isValid) {
            return validationResult;
        }
    }
    // Check if restricted polygons (charge station or work site) are within project area
    // and non-overlap and non-containment with each other
    if (isRestrictedPolygonValidation) {
        validationResult = validateRestrictedPolygons(savedChargeStationOrWorkSitePolygons, validateTurfPolygon, savedProjectAreaPolygons);
        if (!validationResult.isValid) {
            return validationResult;
        }
    }
    return validationResult || { isValid: true, message: "Invalid polygon type" };
};
